import axios from "axios";
import moment from "moment";
import { HTTP_METHOD_TYPES, APP_CONSTANTS } from "../constants/app-constant";
import { getCookie, deleteAllCookies, setCookie } from "./cookieProvider";
import {
  COOKIE,
  HTTP_CODES,
  ERROR_RESPONSE_CODE,
  INVALID_SESSION_KEY,
} from "../constants/app-constant";

const apiCalls = {};
const errorText = APP_CONSTANTS.API_CANCEL_ERROR_TEXT;

const request = (
  method,
  url,
  data = {},
  apiHeaders = {},
  isCancelable,
  apiKey = "",
  timeout = 0
) => {
  let token = getCookie(COOKIE.AUTH_TOKEN);
  let headers = { ...apiHeaders };

  const isFormData = data instanceof FormData;

  // Only set JSON content-type if not FormData and not already defined
  if (!isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (!headers.Authorization && token) {
    if (url && !url.match("/app/")) {
      token = `Bearer ${token}`;
    }
    headers = { ...headers, Authorization: token };
  }

  axios.interceptors.request.use((x) => {
    x.meta = x.meta || {};
    x.meta.requestStartedAt = new Date().getTime();
    return x;
  });

  axios.interceptors.response.use(
    (response) => {
      if (apiCalls[response.request.responseURL]) {
        delete apiCalls[response.request.responseURL];
      }
      return response;
    },
    (error) => {
      try {
        const { status, code } = error.response.data;
        if (
          status === HTTP_CODES.FORBIDDEN &&
          code === ERROR_RESPONSE_CODE.INVALID_TOKEN &&
          token
        ) {
          deleteAllCookies();
          setCookie({ key: INVALID_SESSION_KEY, value: true });
          window.location.href = "/";
        }
      } catch {}
      if (error.message !== errorText) {
        delete apiCalls[apiKey];
      }
      return Promise.reject(error);
    }
  );

  let options = {
    method,
    url,
    data,
    headers,
    timeout,
  };

  if (isCancelable) {
    if (apiCalls[apiKey]) {
      apiCalls[apiKey](errorText);
    }

    options = {
      ...options,
      cancelToken: new axios.CancelToken((c) => {
        apiCalls[apiKey] = c;
      }),
    };
  }

  return axios(options);
};

// HTTP methods
export const httpGet = (
  url,
  headers = {},
  isCancelable = false,
  apiKey = ""
) => {
  return request(HTTP_METHOD_TYPES.GET, url, {}, headers, isCancelable, apiKey);
};

export const httpPost = (
  url,
  data,
  headers = {},
  isCancelable = false,
  apiKey = "",
  timeout
) => {
  return request(
    HTTP_METHOD_TYPES.POST,
    url,
    data,
    headers,
    isCancelable,
    apiKey,
    timeout
  );
};

export const httpPut = (
  url,
  data,
  headers = {},
  isCancelable = false,
  apiKey = ""
) => {
  return request(
    HTTP_METHOD_TYPES.PUT,
    url,
    data,
    headers,
    isCancelable,
    apiKey
  );
};

export const httpDelete = (
  url,
  data,
  headers = {},
  isCancelable = false,
  apiKey = ""
) => {
  return request(
    HTTP_METHOD_TYPES.DELETE,
    url,
    data,
    headers,
    isCancelable,
    apiKey
  );
};

// File download method
export const fileDownloadMethod = (
  url = "",
  payload,
  headers,
  { type, clientName, articleInfoExtra = null }
) => {
  return new Promise((resolve, reject) => {
    fetch(`${url}`, {
      method: HTTP_METHOD_TYPES.POST,
      headers: headers
        ? headers
        : {
            "Content-Type": "application/json",
            Authorization: getCookie(COOKIE.AUTH_TOKEN),
            responseType: "blob",
          },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        const rawTitle = response.headers.get("content-disposition");
        response.blob().then((blob) => {
          const filename = rawTitle
            ? rawTitle
                .replaceAll("attachment; filename=", "")
                .replaceAll("attachment;filename=", "")
                .replace(/"/g, "")
                .toString()
            : type && `Media ${type.toUpperCase()} --${clientName}.${type}`;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          let filenameByArticleInfo = "";
          if (articleInfoExtra) {
            let publishDate = moment(
              articleInfoExtra.publish_date,
              "dddd Do MMMM, YYYY"
            );
            filenameByArticleInfo = `${moment(publishDate).format(
              "Do-MMMM-YYYY"
            )}_${articleInfoExtra.publication}_${articleInfoExtra.edition}_${
              articleInfoExtra.page_nos
            }.${type}`;
          }
          a.href = url;
          a.download = articleInfoExtra ? filenameByArticleInfo : filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          resolve();
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
};
